import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { cloneDeep, compact, groupBy, isEmpty, isEqual, isNil, map, omit, pick } from 'lodash';
import {
  INDIVIDUAL_POP_UP_MODE,
  PAGE_NAMES,
  PROJECT_PEER_EVALUATION_STATUSES,
  PROJECT_ASSESSMENT_STATUSES,
  NOTIFICATION,
  PROJECT_TYPES,
  PROJECT_ASSESSMENT_TOOLTIP,
  USER_ENGAGEMENT_SOURCES_TEXT,
  USER_ENGAGEMENT_SOURCES
} from '@/constants';
import {
  buildUserEngagement,
  buildUserEngagementAction,
  buildUserEngagementFromHRMTimeSheet,
  isValidNumericInputKey,
  userEngagementRoleMapping
} from '@/helpers';
import {
  ActivityKeyMapDTO,
  ActivityKeyMapResponseDTO,
  HRMUserEngagementTimeSheetDTO,
  ProjectAssessmentDTO,
  ProjectRequestDTO,
  TableHeaderColumns,
  UserDTO,
  UserEngagementDTO,
  UserEngagementReceivedDrops,
  UserEngagementRequestDTO
} from '@/models';
import {
  getUserEngagementsReceivedDrops,
  sortArrByNumberField,
  sortArrByTextField,
  sortArrByUserDTOField
} from '@/services';
import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';
import ProjectTeamMemberIndividualPopup from './individual-popup/IndividualPopup.vue';
import UserInformation from '@/components/common/user-information/UserInformation.vue';
import ImportTeamMember from '@/components/pages/project-evaluation/project-team-member/import-team-member/ImportTeamMember.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import Tooltip from '@/components/common/tooltip/Tooltip.vue';
import { Getter } from '@/decorators';
import { remapHrmTimeSheets } from '@/helpers/hrm';
import ProjectTeamMemberForm from './project-team-member-form/ProjectTeamMemberForm.vue';

interface DisplayIndividualDrops {
  value: number;
  adjusted: boolean;
}
interface StatementStatus {
  class: string;
  content: string;
}

@Component({
  components: {
    ConfirmationDialog,
    ImportTeamMember,
    Pagination,
    ProjectTeamMemberForm,
    ProjectTeamMemberIndividualPopup,
    Tooltip,
    UserInformation
  }
})
export default class ProjectTeamMember extends Vue {
  @Prop({ default: false }) canEdit: boolean;
  @Prop({ default: false }) canAdjust: boolean;
  @Prop({ default: false }) isApproveDisabled: boolean;
  @Prop({ default: false }) isConfirmShown: boolean;
  @Prop({ default: false }) isIconApproveShown: boolean;
  @Prop() onStepConfirmed: () => void;
  @Prop() hrmTimeSheets: HRMUserEngagementTimeSheetDTO[];
  @Getter('project/userEngagements') userEngagements: UserEngagementDTO[];
  @Getter('project/hrmEditedTimeSheets') hrmEditedTimeSheets: HRMUserEngagementTimeSheetDTO[];

  $refs!: {
    dialogDeleteRef: HTMLFormElement;
    popupEditRef: HTMLFormElement;
  };

  private currentUser = {} as UserDTO;
  private cancelAddMemberShown = false;
  private members: UserEngagementDTO[] = [];
  private membersReceivedDrops = [] as UserEngagementReceivedDrops[];
  private saveLoading = false;

  private isEditing = false;
  private isEditingIndividualPopup = false;
  private isFetchingHRM = true;
  private individualTrackingPopupShown = false;
  private evaluationConfirmationDialogShown = false;

  private adjustConfirmationDialogShown = false;
  private memberDeletionDialogShown = false;
  private pullDataDialogShown = false;
  private keyMapDialogShown = false;
  private page = 1;
  private pageCount = 0;
  private itemsPerPage = 10;
  private isAutoPullData = false;
  private key = -1;
  private assessmentActivityKey: ActivityKeyMapDTO = {};
  private clonedAssessmentActivityKey: ActivityKeyMapDTO = {};

  hrmMemberTimeSheets: HRMUserEngagementTimeSheetDTO[] = [];
  mode = INDIVIDUAL_POP_UP_MODE.ADD;
  USER_ENGAGEMENT_SOURCES = USER_ENGAGEMENT_SOURCES;
  //#region watch
  @Watch('isEditing')
  setIsProjectEditing(): void {
    this.$store.commit('project/setIsProjectEditing', this.isEditing);
  }

  @Watch('hrmEditedTimeSheets', { deep: true })
  onEditedHrmTimeSheetChanged(): void {
    this.hrmMemberTimeSheets = remapHrmTimeSheets(this.hrmMemberTimeSheets, this.hrmEditedTimeSheets);
  }

  @Watch('members', { deep: true })
  checkSaved(): void {
    this.isEditing = true;
    if (this.members.length === 0) {
      this.key = -1;
    }
    if (this.members.length > 0 && this.isIndividualTrackingProject && this.key <= 0) {
      this.key = Number(this.defaultKey);
    }
  }


  isHrmSource(source?: string): boolean {
    return (
      source === USER_ENGAGEMENT_SOURCES.HRM_DATA ||
      source === USER_ENGAGEMENT_SOURCES.HRM_DATA_EDITED ||
      source === USER_ENGAGEMENT_SOURCES.HRM_DATA_ADD_NEW
    );
  }
  @Watch('key')
  @Watch('assessmentActivityKey', { deep: true })
  onChangeAssessmentActivityKey(): void {
    if (this.isIndividualTrackingProject) {
      const key = +this.key;
      this.members = this.members.map(member => {
        const newDrops = (this.hrmTimeSheetMap[member.user.id!] || []).reduce((acc, timesheet) => {
          const activityKey = Number(this.assessmentActivityKey[timesheet.activityName]) || Number(this.defaultKey) || 1;
          return acc + activityKey * (timesheet?.isApproved === false ? 0 : timesheet.totalHourTask);
        }, 0);
        const activities = Object.keys(this.assessmentActivityKey);
        let activitiesDrops = 0;
        if (member.source === USER_ENGAGEMENT_SOURCES.MANUALLY_INPUT && !isEmpty(member.manuallyInput)) {
          activitiesDrops = activities.reduce((acc, activity) => {
            const contributionHour = Number(member.manuallyInput?.[activity]) || 0;
            const activityKey = Number(this.assessmentActivityKey[activity]) || 0;
            return acc + activityKey * contributionHour;
          }, 0);
        }
        let individualDrops = newDrops;
        if (member.source === USER_ENGAGEMENT_SOURCES.MANUALLY_INPUT) {
          individualDrops = activitiesDrops;
        }
        if (member.source === USER_ENGAGEMENT_SOURCES.IMPORTED_FROM_FILE) {
          individualDrops = key * member.contribution;
        }
        return {
          ...member,
          key,
          individualDrops: individualDrops || newDrops
        };
      });
    }
  }
  //#endregion

  //#region build
  buildTableHeaderColumns(hasActions: boolean): TableHeaderColumns {
    const headerColumns = [
      {
        text: 'Member',
        value: 'user',
        class: 'grey--text'
      },
      {
        text: 'Role',
        value: 'userEngagementRole',
        class: 'grey--text'
      },
      {
        align: 'end',
        text: 'Contribution (hour)',
        value: 'contribution',
        class: 'grey--text'
      },
      {
        align: 'end',
        text: 'Contribution (%)',
        value: 'contributionPercent',
        class: 'grey--text'
      },
      {
        align: 'end',
        text: 'Contribution Pollens',
        value: 'contributionDrops',
        class: 'grey--text'
      },
      {
        align: 'end',
        text: 'Pollens',
        value: 'key',
        class: 'grey--text'
      },
      {
        align: 'end',
        text: 'Individual Pollens',
        value: 'individualDrops',
        class: 'grey--text'
      },
      {
        align: 'end',
        text: 'Note',
        value: 'note',
        class: 'grey--text'
      }
    ];
    if (this.isIndividualTrackingProject) {
      headerColumns.splice(2, 0, {
        text: 'Source',
        value: 'source',
        class: 'grey--text'
      });
    }
    if (hasActions) {
      return [
        ...headerColumns,
        {
          align: 'end',
          value: 'actions',
          sortable: false
        }
      ];
    }
    return headerColumns;
  }

  buildMembersFromTimeSheetData(hrmMemberTimeSheets: HRMUserEngagementTimeSheetDTO[]): UserEngagementDTO[] {
    const hrmMemberTimeSheetsGrouped = groupBy(hrmMemberTimeSheets, 'user.code');
    const userEngagements = Object.values(hrmMemberTimeSheetsGrouped).map(t => {
      const contribution = t.reduce((acc, cur) => acc + cur.totalHourTask, 0);
      return buildUserEngagementFromHRMTimeSheet(t[0], contribution);
    });
    return userEngagements;
  }

  //#endregion

  //#region computed
  get individualPopupMode(): typeof INDIVIDUAL_POP_UP_MODE {
    return INDIVIDUAL_POP_UP_MODE;
  }

  get defaultKey(): number {
    if (this.members && this.members.length > 0) {
      return compact(map(this.members, 'key'))[0] || this.keyIndividualFromAssessment;
    }
    return this.keyIndividualFromAssessment;
  }
  get projectAssessment(): ProjectAssessmentDTO {
    return this.$store.getters['project/projectAssessment'];
  }

  get hrmTimeSheetMap(): { [key: number]: HRMUserEngagementTimeSheetDTO[] } {
    return groupBy(this.hrmMemberTimeSheets, 'user.id');
  }

  get existingActivityKeyMap(): ActivityKeyMapDTO {
    return (this.projectAssessment.activityKeyMap || []).reduce((acc, item: ActivityKeyMapResponseDTO) => {
      acc[item.activityName] = item.keyValue;
      return acc;
    }, {} as ActivityKeyMapDTO);
  }

  get isAdjustConfirmDisabled(): boolean {
    return (
      this.userEngagements.filter(ue => ue.peerEvaluationStatus === PROJECT_PEER_EVALUATION_STATUSES.STARTED.label)
        .length > 0
    );
  }

  get isConfirmDisabled(): boolean {
    return this.members.length <= 0 || !this.key || isNaN(+this.key) || !+this.key;
  }

  get isIndividualTrackingProject(): boolean {
    return this.$store.getters['project/projectType'] === PROJECT_TYPES.INDIVIDUAL_TRACKING;
  }

  get keyIndividualFromAssessment(): number {
    return this.$store.getters['project/projectAssessment'].key;
  }

  get totalContributionHours(): number {
    return this.members.reduce((acc, curr) => acc + curr.contribution, 0);
  }

  get tableHeaderColumns(): TableHeaderColumns {
    const isResultRelease =
      this.$store.getters['project/assessmentStatus'] === PROJECT_ASSESSMENT_STATUSES.RESULT_RELEASE;

    if (this.isIndividualTrackingProject) {
      return this.buildTableHeaderColumns(this.canEdit || this.canAdjust || isResultRelease).filter(header => {
        return !(header.value === 'contributionPercent') && !(header.value === 'contributionDrops');
      });
    }
    return this.buildTableHeaderColumns(this.canEdit || this.canAdjust || isResultRelease).filter(header => {
      return !(header.value === 'note') && !(header.value === 'key');
    });
  }

  get isMemberContributionEvaluation(): boolean {
    return (
      this.$store.getters['project/assessmentStatus'] === PROJECT_ASSESSMENT_STATUSES.MEMBER_CONTRIBUTION_EVALUATION
    );
  }

  get isProjectTeamMemberEmpty(): boolean {
    return this.members.length === 0;
  }

  get projectInformation(): ProjectRequestDTO {
    return this.$store.getters['project/project'];
  }

  get isDataOutdatedFromHRM(): boolean {
    if (isEmpty(this.hrmMembers)) {
      return false;
    } else {
      if (isEmpty(this.members)) {
        return this.hrmMembers.length > 0;
      }
      const isValidAmount = this.hrmMembers.length <= this.members.length;
      if (!isValidAmount) {
        return true;
      }
      const isDataOutdatedWithHRM =
        !!this.hrmMembers &&
        this.hrmMembers.some(hrmMember => {
          const { user } = hrmMember;
          const userEngagement = this.members.find(member => member.user.id === user.id);
          if (userEngagement && !this.isHrmSource(userEngagement.source)) {
            return true;
          } else {
            return false;
          }
        });
      return isDataOutdatedWithHRM;
    }
  }

  get isLinkedWithHRM(): boolean {
    return !!this.projectInformation.hrmProjectId;
  }

  get hrmStatement(): StatementStatus {
    if (this.isAutoPullData && this.isEditing && !this.isDataOutdatedFromHRM) {
      this.isAutoPullData = false;
      return {
        class: 'green--text',
        content: 'Member timesheets are automatically pulled from HRM. Click SAVE button to store them.'
      };
    }

    return {
      class: this.isDataOutdatedFromHRM ? 'orange--text' : 'green--text',
      content: this.isDataOutdatedFromHRM
        ? 'Your data is outdated from HRM data. Click PULL FROM HRM button to pull the latest version.'
        : this.isFetchingHRM
        ? ''
        : 'Your data is up to date with the latest HRM data.'
    };
  }

  get keyTooltipValue(): string {
    return PROJECT_ASSESSMENT_TOOLTIP.KEY;
  }

  get hrmMembers(): UserEngagementDTO[] {
    return this.buildMembersFromTimeSheetData(this.hrmMemberTimeSheets);
  }

  get disableSave(): boolean {
    const cloneMembers = cloneDeep(this.members).map(member => omit(member, 'key'));
    return isEqual(cloneMembers, this.userEngagements) || !this.isEditing;
  }

  get latestIndividualKey(): number {
    const projectAssessments: ProjectAssessmentDTO[] = this.$store.getters['project/projectAssessments'];
    return projectAssessments?.filter(a => a.key)?.[0]?.key ?? -1;
  }
  //#endregion

  //#region table helper
  pendingAvatarStatus(user: UserDTO): boolean | undefined {
    const userEngagements = this.projectAssessment?.userEngagements || [];
    const checkUser = userEngagements.find(userEngagement => userEngagement.user.id === user.id);
    if (this.isIndividualTrackingProject) {
      return false;
    }
    if (this.canEdit) {
      return undefined;
    }
    if (checkUser) {
      return checkUser.peerEvaluationStatus !== PROJECT_PEER_EVALUATION_STATUSES.DONE.label;
    }
    return false;
  }

  isMemberFromHrm(userEngagement: UserEngagementDTO): boolean {
    return this.isLinkedWithHRM && this.isHrmSource(userEngagement.source);
  }
  //#endregion

  //#region custom column
  getContributionPercent(member: UserEngagementDTO): string {
    const percent = (member.contribution / this.totalContributionHours) * 100;
    return isNaN(percent) ? '0.00%' : percent.toFixed(2) + '%';
  }

  getRoleText(userEngagementRole: string): string {
    return userEngagementRoleMapping[userEngagementRole];
  }

  getDisplayIndividualDrops(member: UserEngagementDTO): DisplayIndividualDrops {
    const { adjustedDrops, user } = member;
    const receivedDrops = this.getReceivedDrops(user);
    return {
      value: this.getIndividualDrops(member),
      adjusted: receivedDrops !== adjustedDrops && adjustedDrops > 0
    };
  }

  isEmpty(obj: any): boolean {
    return isEmpty(obj);
  }

  getSourceText(source: string): string {
    return USER_ENGAGEMENT_SOURCES_TEXT[source] || '';
  }
  //#endregion

  //#region temp-data
  private memberToEdit = buildUserEngagement();

  resetAddMember(): void {
    this.setCurrentMembers();
    this.cancelAddMemberShown = false;
    this.assessmentActivityKey = cloneDeep(this.clonedAssessmentActivityKey);
    this.$nextTick(() => {
      this.isEditing = false;
    });
  }

  resetEditMember(): void {
    this.$nextTick(() => {
      this.memberToEdit = buildUserEngagement();
    });
  }

  //#region delete
  openDialogDelete(member: UserEngagementDTO): void {
    this.memberToEdit = member;
    this.memberDeletionDialogShown = true;
  }

  deleteMember(): void {
    this.members = this.members.filter(member => {
      return member.user.id !== this.memberToEdit.user.id;
    });
    this.resetEditMember();
    this.memberDeletionDialogShown = false;
  }

  //#endregion

  //#region edit
  editMember(editedMember: UserEngagementDTO): void {
    this.members = this.members.map(member => (member.user.id === this.memberToEdit.user.id ? editedMember : member));
    this.resetEditMember();
    if (this.isIndividualTrackingProject) {
      this.isEditingIndividualPopup = true;
    }
  }
  //#endregion

  //#region add
  addMember(newMember: UserEngagementDTO): void {
    this.members = [...this.members, newMember];
    this.individualTrackingPopupShown = false;
  }
  //#endregion

  // #region keyMap dialog

  openKeyMap(): void {
    this.keyMapDialogShown = true;
  }

  onCancelKeyMap(): void {
    this.keyMapDialogShown = false;
    // this.assessmentActivityKey = cloneDeep(this.clonedAssessmentActivityKey);
  }

  onSaveKeyMap(): void {
    this.keyMapDialogShown = false;
  }

  onManagerApprove(): void {
    this.$emit('onManagerApprove');
  }

  // #endregion
  cancelAddMembers(): void {
    this.cancelAddMemberShown = true;
  }

  customSort(items: UserEngagementDTO[], fields: string[], isDescArr: boolean[]): UserEngagementDTO[] {
    const field = fields[0];
    if (field) {
      const isDesc = isDescArr[0];
      switch (field) {
        case 'user': {
          return sortArrByUserDTOField(items, 'user', isDesc);
        }
        case 'userEngagementRole': {
          return sortArrByTextField(items, 'userEngagementRole', isDesc);
        }
        case 'contributionPercent': {
          return sortArrByNumberField(items, 'contribution', isDesc);
        }
        case 'contribution':
        case 'contributionDrops':
        case 'individualDrops': {
          return sortArrByNumberField(items, field, isDesc);
        }
        default: {
          return items;
        }
      }
    }
    return items;
  }

  closeIndividualPopup(): void {
    this.individualTrackingPopupShown = false;
    this.isEditingIndividualPopup = false;
  }

  isNewMemberExist(newMember: UserEngagementDTO): boolean {
    return (
      this.members.filter(member => {
        return member.user.id === newMember.user.id;
      }).length > 0
    );
  }

  setCurrentMembers(): void {
    this.members = this.$store.getters['project/userEngagements'];
    if (this.isIndividualTrackingProject && this.keyIndividualFromAssessment) {
      this.members = this.members.map(member => ({ ...member, key: this.keyIndividualFromAssessment }));
    }
  }

  getReceivedDrops(user: UserDTO): number {
    return this.membersReceivedDrops.find(member => member[user.id])?.[user.id] ?? 0;
  }

  getIndividualDrops(member: UserEngagementDTO): number {
    const { contributionDrops, givingDrops, adjustedDrops, user } = member;
    return contributionDrops - givingDrops + (adjustedDrops > 0 ? adjustedDrops : this.getReceivedDrops(user));
  }

  submitMemberIndividual(editedMember: UserEngagementDTO): void {
    this.memberToEdit = editedMember;
    if (this.mode === INDIVIDUAL_POP_UP_MODE.ADD) {
      if (!this.isNewMemberExist(editedMember)) {
        this.members = [...this.members, editedMember];
      }
    } else if (this.mode === INDIVIDUAL_POP_UP_MODE.EDIT) {
      this.members = this.members.reduce<UserEngagementDTO[]>((result, member) => {
        if (member.user.id === editedMember.user.id) {
          const shouldKeepMember = editedMember.contribution > 0 || member.contributionDrops > 0;
          if (shouldKeepMember) {
            result.push(editedMember);
          }
        } else {
          result.push(member);
        }
        return result;
      }, []);
      
    }
    this.closeIndividualPopup();
  }

  navigateToAdjustIndividualDropsPage(): void {
    const { id } = this.$route.params;
    this.$router.push({
      name: PAGE_NAMES.ADJUST_INDIVIDUAL_DROPS,
      params: {
        projectId: '' + id,
        assessmentId: '' + this.projectAssessment.id
      }
    });
  }

  openPullDataDialog(): void {
    if (this.members.length === 0) {
      this.pullDataFromHRM();
    } else {
      this.pullDataDialogShown = true;
    }
  }

  closePullDataDialog(): void {
    this.pullDataDialogShown = false;
  }

  async onAdjustConfirm(): Promise<void> {
    const membersWithIndividualDrops = this.members.map(member => ({
      ...member,
      individualDrops: this.getIndividualDrops(member)
    }));
    await this.$store.dispatch('project/asyncUpdateProjectPeerEvaluation', {
      members: buildUserEngagementAction(membersWithIndividualDrops),
      projectAssessmentId: this.projectAssessment.id,
      confirm: true
    });
    this.adjustConfirmationDialogShown = false;
    this.$emit('onStepConfirmed');
  }

  async onViewDetails(userEngagement: UserEngagementDTO): Promise<void> {
    const assessmentId = this.projectAssessment.id;
    const projectId = this.$store.getters['project/project']?.id;
    await this.$router.push({
      name: PAGE_NAMES.USER_EVALUATION_DETAILS,
      params: {
        projectId: '' + projectId,
        projectAssessmentId: '' + assessmentId,
        userId: '' + userEngagement.user.id
      }
    });
  }

  async saveMembers(confirm: boolean): Promise<void> {
    const individualTrackingListKey = [
      'contribution',
      'contributionDrops',
      'individualDrops',
      'key',
      'userEngagementRole',
      'userId',
      'source',
      'note',
      'hrmTimeSheets',
      'manuallyInput'
    ];
    this.saveLoading = true;
    try {
      const hrmTimeSheetMap = groupBy(this.hrmMemberTimeSheets, 'user.id');
      await this.$store.dispatch('project/asyncUpdateProjectTeamMembers', {
        members: buildUserEngagementAction(this.members).map(member => {
          if (this.isIndividualTrackingProject) {
            member.contributionDrops = member.individualDrops || 0;
            member = pick(member, individualTrackingListKey) as UserEngagementRequestDTO;
            if (member.source?.includes('HRM') && hrmTimeSheetMap[member.userId]) {
              const newDrops = hrmTimeSheetMap[member.userId].reduce((acc, timesheet) => {
                const activityKey = Number(this.assessmentActivityKey[timesheet.activityName]) || Number(this.defaultKey) || 1;
                return acc + activityKey * (timesheet?.isApproved === false ? 0 : timesheet.totalHourTask);
              }, 0);
              member.contributionDrops = newDrops;
              member.individualDrops = newDrops;
              member.hrmTimeSheets = hrmTimeSheetMap[member.userId].map(v => ({
                ...v,
                isApproved: isNil(v?.isApproved) ? true : v?.isApproved
              }));
            }
          }
          return member;
        }),
        projectAssessmentId: this.projectAssessment.id,
        confirm,
        key: this.members[0]?.key,
        activityKeyMap: this.assessmentActivityKey
      });
      this.saveLoading = false;
      this.isEditing = false;
      this.closeIndividualPopup();
      if (confirm) {
        const data = {
          showing: true,
          type: NOTIFICATION.TYPE.success,
          title: NOTIFICATION.TEXT.confirmMemberContributionEvaluationSuccess,
          content: [],
          icon: NOTIFICATION.ICON.success,
          multiline: false,
          height: 0
        };
        this.$store.commit('notification/setNotification', data);
        this.$emit('onStepConfirmed');
      }
    } catch (e) {
      this.saveLoading = false;
      this.evaluationConfirmationDialogShown = false;

      let text;
      if (confirm) {
        text = NOTIFICATION.TEXT.confirmMemberContributionEvaluationFailed;
      } else {
        text = NOTIFICATION.TEXT.saveMemberContributionEvaluationFailed;
      }

      const data = {
        showing: true,
        type: NOTIFICATION.TYPE.error,
        title: text,
        content: [],
        icon: NOTIFICATION.ICON.error,
        multiline: false,
        height: 0
      };
      this.$store.commit('notification/setNotification', data);
    }
  }

  async pullDataFromHRM(): Promise<void> {
    this.key = -1;
    this.members = cloneDeep(this.hrmMembers);
    this.pullDataDialogShown = false;
  }

  onImportSuccess(members: UserEngagementDTO[]): void {
    if (this.isIndividualTrackingProject) {
      this.key = -1;
    }
    this.members = members;
  }

  onClearAll(): void {
    this.members = [];
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }

  autoPullDataFromHRM(): void {
    this.pullDataFromHRM();
    this.isEditing = true;
    this.isAutoPullData = true;
  }

  onKeyDown(event: KeyboardEvent): void {
    const value = (event.target as any).value;
    if (!isValidNumericInputKey(event)) {
      event.preventDefault();
    }
    if (value && /^0/.test(value)) {
      this.key = value.replace(/^0/, '');
    }
  }

  async mounted(): Promise<void> {
    if (!this.isAdjustConfirmDisabled) {
      const { data } = await getUserEngagementsReceivedDrops(this.projectAssessment.id);
      this.membersReceivedDrops = data;
    }
  }
  
  async created(): Promise<void> {
    this.currentUser = this.$store.getters['app/currentUser'];
    this.setCurrentMembers();

    this.$nextTick(() => {
      this.isEditing = false;
    });

    if (this.isLinkedWithHRM) {
      this.hrmMemberTimeSheets = remapHrmTimeSheets(this.hrmTimeSheets, this.hrmEditedTimeSheets);
      if (!this.members.length && !!this.hrmMembers) {
        this.autoPullDataFromHRM();
      }
    }

    if (this.isIndividualTrackingProject) {
      if (this.key === -1) {
        this.key = this.latestIndividualKey;
      }

      const assessmentActivityKey = (this.projectInformation?.activities || []).reduce((acc, activity) => {
        const existingActivityKey = this.existingActivityKeyMap[activity];
        acc[activity] = existingActivityKey;
        return acc;
      }, {} as { [key: string]: number });
      this.assessmentActivityKey = assessmentActivityKey;
      this.clonedAssessmentActivityKey = cloneDeep(assessmentActivityKey);
    }

    this.isFetchingHRM = false;
  }

  //#region handle popup
  onPopupOpen(member: UserEngagementDTO, mode?: string): void {
    if (mode === INDIVIDUAL_POP_UP_MODE.VIEW) {
      this.isEditingIndividualPopup = true;
      this.individualTrackingPopupShown = true;
      this.mode = INDIVIDUAL_POP_UP_MODE.VIEW;
      this.memberToEdit = member;
      return;
    }
    if (this.isIndividualTrackingProject) {
      this.isEditingIndividualPopup = true;
      this.individualTrackingPopupShown = true;
      if (isEmpty(member)) {
        this.mode = INDIVIDUAL_POP_UP_MODE.ADD;
      } else {
        this.memberToEdit = member;
        this.mode = mode || INDIVIDUAL_POP_UP_MODE.EDIT;
      }
    } else {
      if (member) {
        this.memberToEdit = member;
        this.$refs.popupEditRef.openMenu(member);
      }
    }
  }
  //#endregion
}
